import { usePartnerId } from "@simble/shared/hooks/use-partner-id";
import { postPartnersAuth } from "@simble/shared/hooks/use-profile-api";
import { setCookie } from "@simble/shared/lib/cookie-utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import React from "react";
import { getRefreshToken, refreshAuthToken, setRefreshToken } from "@/services/finuslugi-auth";
import { store } from "@/store/local-store";

const PASSWORD = "fr$F%$w%-yQ^bb}3q9|NHhe+[_h!rcX#";
const SALT = "3ecd3ac05cc791418fc7e13eb46beaea";

const encoder = new TextEncoder();
const decoder = new TextDecoder();

const getKeyMaterial = (pass: string) => crypto.subtle.importKey("raw", encoder.encode(pass), "PBKDF2", false, ["deriveKey"]);

const deriveAesKey = async (pass: string, saltValue: string) => {
  const keyMaterial = await getKeyMaterial(pass);
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: encoder.encode(saltValue),
      iterations: 100_000,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
};

const decryptData = async (b64: string, key: CryptoKey) => {
  const combined = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  const iv = combined.slice(0, 12);
  const data = combined.slice(12);
  const decrypted = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, data);
  return decoder.decode(decrypted);
};

const parseDecryptedData = (decrypted: string): Record<string, unknown> | null => {
  try {
    return JSON.parse(decrypted);
  } catch (e) {
    console.error("Ошибка парсинга данных:", e);
    return null;
  }
};

const getStringValue = (value: unknown): string | undefined => (typeof value === "string" ? value : undefined);

const getBooleanValue = (value: unknown): boolean | undefined => (typeof value === "boolean" ? value : undefined);

const assignStoreData = (data: Record<string, unknown>, partnerUsername: string) => {
  const fio = getStringValue(data.fio);
  const phoneNum = getStringValue(data.phoneNum);
  const email = getStringValue(data.email);
  const dateOfBirth = getStringValue(data.dateOfBirth);
  const experienceStartDate = getStringValue(data.experienceStartDate);
  const passport = getStringValue(data.passport);
  const noSurname = getBooleanValue(data.noMiddleName);
  const grz = getStringValue(data.gosNumber) || "";
  const passportDate = getStringValue(data.DateOfLastLicense);

  store.assign({
    ...(fio !== undefined && { fio }),
    ...(phoneNum !== undefined && { phoneNum }),
    ...(email !== undefined && { email }),
    ...(dateOfBirth !== undefined && { dateOfBirth }),
    ...(experienceStartDate !== undefined && { experienceStartDate }),
    ...(passport !== undefined && { passport }),
    ...(noSurname !== undefined && { noSurname }),
    grz,
    ...(passportDate !== undefined && { passportDate }),
    userName: partnerUsername || "",
  });
};

const decryptHash = async (hashValue: string) => {
  const key = await deriveAesKey(PASSWORD, SALT);
  const decodedUri = decodeURIComponent(hashValue);
  const decrypted = await decryptData(decodedUri, key);
  return parseDecryptedData(decrypted);
};

const LoginPageWithData = () => {
  const { hash } = Route.useParams();
  const partnerIdMutation = usePartnerId({
    onSuccess: (data) => {
      store.partnerId.set(data.id);
    },
  });
  const navigate = useNavigate();
  const processedRef = React.useRef<string | null>(null);

  const mutatePartnerId = React.useCallback(
    async (username: string) => {
      await partnerIdMutation.mutateAsync({
        username,
        channel_id: Number.parseInt(import.meta.env.VITE_PARTNER_CHANNEL_ID || "0", 10),
      });
    },
    [partnerIdMutation.mutateAsync]
  );

  const tryRestoreSessionByRefresh = React.useCallback(async (): Promise<boolean> => {
    if (!getRefreshToken()) {
      return false;
    }
    const newToken = await refreshAuthToken();
    return Boolean(newToken);
  }, []);

  React.useEffect(() => {
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <Complex decryption and data processing logic is necessary>
    const processHash = async () => {
      if (!hash) {
        navigate({ to: "/" });
        return;
      }

      if (processedRef.current === hash) {
        return;
      }

      processedRef.current = hash;

      // Если есть refresh_token — пробуем обновить сессию; при успехе редирект на главную
      if (await tryRestoreSessionByRefresh()) {
        navigate({ to: "/" });
        return;
      }

      try {
        const data = await decryptHash(hash);

        if (!data) {
          navigate({ to: "/" });
          return;
        }

        const partnerUsername =
          typeof data.partnerUsername === "string" || typeof data.partnerUsername === "number" ? String(data.partnerUsername) : null;

        if (partnerUsername) {
          await mutatePartnerId(partnerUsername);
        }

        const hasPartnerId = store.partnerId.peek() && partnerUsername;

        if (!hasPartnerId) {
          console.error("Не удалось получить partnerId");
          navigate({ to: "/" });
          return;
        }

        if (partnerUsername) {
          assignStoreData(data, partnerUsername);
        }

        const authResponse = await postPartnersAuth("finuslugi");

        setCookie("auth_token", authResponse.token);
        setRefreshToken(authResponse.refresh_token);

        navigate({ to: "/" });
      } catch (error) {
        console.error("Ошибка расшифровки данных:", error);
        navigate({ to: "/" });
      }
    };

    processHash();
  }, [hash, navigate, mutatePartnerId, tryRestoreSessionByRefresh]);

  return null;
};

export const Route = createFileRoute("/login/$hash")({
  component: LoginPageWithData,
});
